import type { ApiResponse, PaginatedResponse } from '@/types'

export const toFiniteNumber = (value: unknown, fallback: number): number => {
  if (value === null || value === undefined) {
    return fallback
  }

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

const extractApiErrorMessage = (error: unknown): string | undefined => {
  if (!error) {
    return undefined
  }

  if (typeof error === 'string') {
    return error
  }

  if (
    typeof error === 'object' &&
    'message' in (error as any) &&
    typeof (error as any).message === 'string'
  ) {
    return (error as any).message
  }

  return undefined
}

export const normalizeApiPagination = (
  rawPagination: any,
  defaults: { page?: number; limit?: number; total?: number } = {}
) => {
  const page = toFiniteNumber(rawPagination?.page, defaults.page ?? 1)
  const limit = toFiniteNumber(rawPagination?.limit, defaults.limit ?? 20)
  const total = toFiniteNumber(rawPagination?.total, defaults.total ?? 0)
  const totalPagesRaw =
    rawPagination?.totalPages ?? rawPagination?.total_pages ?? rawPagination?.pages
  const pages = Number.isFinite(totalPagesRaw)
    ? Number(totalPagesRaw)
    : limit > 0
      ? Math.ceil(total / limit)
      : 0

  return {
    page,
    limit,
    total,
    pages,
    total_pages: rawPagination?.total_pages ?? pages,
  }
}

type ExtractArrayOptions = {
  keys?: string[]
  candidateKeys?: string[]
  includePagination?: boolean
  preferExplicitKeys?: boolean
}

export const extractArrayFromApiPayload = <T>(
  payload: any,
  options: ExtractArrayOptions = {}
): T[] => {
  const {
    keys = [],
    candidateKeys = [],
    includePagination = false,
    preferExplicitKeys = false,
  } = options
  const candidates: any[] = []

  if (payload?.data !== undefined) {
    candidates.push(payload.data)
  }

  if (payload !== undefined) {
    for (const key of candidateKeys) {
      if (!key) continue
      candidates.push(payload[key], payload.data?.[key])
    }
  }

  for (const key of keys) {
    if (!key) continue
    if (preferExplicitKeys) {
      if (payload?.data && payload.data[key] !== undefined) {
        candidates.unshift(payload.data[key])
      }
      if (payload && payload[key] !== undefined) {
        candidates.unshift(payload[key])
      }
      continue
    }

    if (payload && payload[key] !== undefined) {
      candidates.push(payload[key])
    }
    if (payload?.data && payload.data[key] !== undefined) {
      candidates.push(payload.data[key])
    }
  }

  if (includePagination) {
    if (payload?.pagination?.items) {
      candidates.push(payload.pagination.items)
    }

    if (payload?.pagination?.data) {
      candidates.push(payload.pagination.data)
    }
  }

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate as T[]
    }
  }

  return []
}

const resolvePaginationSource = (payloadAny: any) =>
  payloadAny?.pagination ??
  payloadAny?.data?.pagination ??
  payloadAny?.data?.data?.pagination ??
  null

export const buildPaginatedResponse = <T>(
  payload: ApiResponse<any> | undefined,
  items: T[],
  options: { defaultPage?: number; defaultLimit?: number } = {}
): PaginatedResponse<T> => {
  const success = Boolean(payload?.success)
  const payloadAny = payload as any
  const pagination = normalizeApiPagination(resolvePaginationSource(payloadAny), {
    page: options.defaultPage ?? 1,
    limit: options.defaultLimit ?? 20,
    total: items.length,
  })
  const errorMessage = success ? undefined : extractApiErrorMessage(payloadAny?.error)

  return {
    success,
    data: Array.isArray(items) ? (items as T[]) : [],
    error: errorMessage,
    message: payloadAny?.message ?? (success ? undefined : errorMessage),
    pagination,
  }
}
